// AI assist bar for the reply composer and the Compose dialog.
//
// One short instruction box that steers Draft and Polish, one-click restyle
// presets, and the agent's own saved prompts. It only fills the composer; a
// human edits and sends (CLAUDE.md section 2).
//
//   [ Tell the AI what to say, or how to change it…   ] [Draft] [Polish] [Undo]
//   Restyle  Fix grammar only · Shorter · Friendlier · More formal · Firmer · Plain English
//   Mine     Ask for screenshot ✕ · Confirm refund timeline ✕ · ＋ Save current
//
// All the behaviour lives in `useAiAssist`; this is presentation + saved prompts.

import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Sparkles, Undo2, Wand2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PolishPreset } from '@/lib/api';
import { POLISH_PRESET_LABELS, type useAiAssist } from '@/lib/use-ai-assist';
import {
  useAiQuickPrompts,
  useDeleteAiQuickPrompt,
  useMyTeamMember,
  useSaveAiQuickPrompt,
} from '@/lib/queries';
import { toastError } from '@/lib/toast';

type Assist = ReturnType<typeof useAiAssist>;

const PRESET_ORDER: PolishPreset[] = [
  'fix_grammar',
  'shorter',
  'friendlier',
  'formal',
  'firmer',
  'plain',
];

const PLACEHOLDER_REPLY =
  'Tell the AI what to say, e.g. "refund approved, lands Friday", or how to change your text';
const PLACEHOLDER_COMPOSE =
  'Tell the AI what the email should say, e.g. "ask for a screenshot and the login email"';

export function AiAssistBar({
  assist,
  hasBodyText,
  isCompose = false,
  disabled = false,
}: {
  assist: Assist;
  /** True when the editor has text, which enables Polish and the Restyle chips. */
  hasBodyText: boolean;
  isCompose?: boolean;
  /** Extra busy state from the parent (e.g. a send is in flight). */
  disabled?: boolean;
}) {
  const [instruction, setInstruction] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveLabel, setSaveLabel] = useState('');

  const promptsQ = useAiQuickPrompts();
  const saveMut = useSaveAiQuickPrompt();
  const deleteMut = useDeleteAiQuickPrompt();
  const myTmQ = useMyTeamMember();
  const prompts = promptsQ.data ?? [];

  const blocked = assist.busy || disabled;
  const trimmed = instruction.trim();

  // Enter = the sensible default: Polish when there is text to work on, Draft
  // when the body is empty.
  const runDefault = () => {
    if (blocked) return;
    if (hasBodyText) assist.polish({ instruction: trimmed });
    else assist.draft({ instruction: trimmed });
  };

  const startSave = () => {
    if (!trimmed) return;
    setSaveLabel(trimmed.slice(0, 28));
    setSaving(true);
  };

  const commitSave = async () => {
    const label = saveLabel.trim();
    const owner = myTmQ.data?.slack_user_id;
    if (!label || !trimmed) return;
    if (!owner) {
      toast.error('Could not work out who you are. Reload and try again.');
      return;
    }
    try {
      await saveMut.mutateAsync({ ownerSlackId: owner, label, instruction: trimmed });
      setSaving(false);
      setSaveLabel('');
      toast.success(`Saved "${label}"`);
    } catch (err) {
      toastError('Could not save prompt', err);
    }
  };

  const chip =
    'inline-flex h-6 cursor-pointer items-center gap-1 rounded-full border border-border bg-background px-2 text-3xs font-medium text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <div
      className="flex flex-col gap-1.5 border-t border-border/60 bg-muted/30 px-2.5 py-2"
      data-ai-assist-bar
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <input
          type="text"
          value={instruction}
          maxLength={1000}
          onChange={(e) => setInstruction(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              runDefault();
            }
          }}
          placeholder={isCompose ? PLACEHOLDER_COMPOSE : PLACEHOLDER_REPLY}
          aria-label="Instruction for the AI"
          className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1 text-xs outline-none placeholder:text-muted-foreground focus:border-foreground/40 focus:ring-1 focus:ring-foreground/15"
        />
        <button
          type="button"
          onClick={() => assist.draft({ instruction: trimmed })}
          disabled={blocked}
          title={
            hasBodyText && !isCompose
              ? 'Write a reply. What you have typed is used as your notes.'
              : 'Write a reply from the conversation and your instruction.'
          }
          className={cn(chip, 'h-7 px-2.5 text-2xs text-foreground')}
        >
          {assist.drafting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
          Draft
        </button>
        <button
          type="button"
          onClick={() => assist.polish({ instruction: trimmed })}
          disabled={blocked || !hasBodyText}
          title="Rewrite what you have written. Your instruction (if any) says how."
          className={cn(chip, 'h-7 px-2.5 text-2xs text-foreground')}
        >
          {assist.polishing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
          Polish
        </button>
        {assist.canUndo ? (
          <button
            type="button"
            onClick={assist.undo}
            disabled={assist.busy}
            title="Restore your text from before the last AI change"
            className={cn(chip, 'h-7 px-2.5 text-2xs text-foreground')}
          >
            <Undo2 className="h-3 w-3" />
            Undo
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-1">
        <span className="mr-0.5 w-12 shrink-0 text-3xs font-medium uppercase tracking-wide text-muted-foreground">
          Restyle
        </span>
        {PRESET_ORDER.map((p) => (
          <button
            key={p}
            type="button"
            disabled={blocked || !hasBodyText}
            onClick={() => assist.polish({ preset: p, instruction: trimmed })}
            title={hasBodyText ? `Rewrite your text: ${POLISH_PRESET_LABELS[p]}` : 'Write something first'}
            className={chip}
          >
            {POLISH_PRESET_LABELS[p]}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-1">
        <span className="mr-0.5 w-12 shrink-0 text-3xs font-medium uppercase tracking-wide text-muted-foreground">
          Mine
        </span>
        {prompts.map((p) => (
          <span key={p.id} className="inline-flex items-center">
            <button
              type="button"
              onClick={() => setInstruction(p.instruction)}
              title={p.instruction}
              className={cn(chip, 'rounded-r-none border-r-0')}
            >
              {p.label}
            </button>
            <button
              type="button"
              aria-label={`Delete saved prompt ${p.label}`}
              title="Delete this saved prompt"
              disabled={deleteMut.isPending}
              onClick={() =>
                deleteMut.mutate(p.id, {
                  onError: (err) => toastError('Could not delete prompt', err),
                })
              }
              className={cn(chip, 'rounded-l-none px-1')}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}

        {saving ? (
          <span className="inline-flex items-center gap-1">
            <input
              autoFocus
              value={saveLabel}
              maxLength={60}
              onChange={(e) => setSaveLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void commitSave();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setSaving(false);
                }
              }}
              placeholder="Name this prompt"
              aria-label="Name for the saved prompt"
              className="h-6 w-36 rounded-md border border-border bg-background px-2 text-3xs outline-none focus:border-foreground/40"
            />
            <button
              type="button"
              className={chip}
              disabled={saveMut.isPending || saveLabel.trim().length === 0}
              onClick={() => void commitSave()}
            >
              Save
            </button>
            <button type="button" className={chip} onClick={() => setSaving(false)}>
              Cancel
            </button>
          </span>
        ) : (
          <button
            type="button"
            className={chip}
            disabled={trimmed.length === 0}
            onClick={startSave}
            title={
              trimmed.length === 0
                ? 'Type an instruction above, then save it for re-use'
                : 'Save the instruction above as a one-click prompt'
            }
          >
            <Plus className="h-3 w-3" />
            Save current
          </button>
        )}
        {prompts.length === 0 && !saving && !promptsQ.isLoading ? (
          <span className="text-3xs text-muted-foreground">
            Save instructions you use a lot, like "ask for a screenshot and the login email".
          </span>
        ) : null}
      </div>
    </div>
  );
}
